import React, { useState, useEffect } from 'react';
import { FiX, FiEye, FiClock, FiUser, FiBox, FiFileText } from 'react-icons/fi';
import requestsService from '../../services/requests.service';
import Swal from 'sweetalert2';
import SectionHelpModal from '../../components/common/SectionHelpModal';
import { useNavigate, useLocation } from 'react-router-dom';
import AppTour from '../../components/common/AppTour';
import { useAuth } from '../../context/AuthContext';
import Pagination from '../../components/common/Pagination';

const RequestsPanel = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useAuth();
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [runTour, setRunTour] = useState(false);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (params.get('tour') === 'solicitudes') {
            setRunTour(true);
        }
    }, [location.search]);

    useEffect(() => {
        loadRequests(currentPage);
    }, [currentPage]);

    const loadRequests = async (page = 1) => {
        try {
            setLoading(true);
            const data = await requestsService.getAll({ page, estado: 'PENDIENTE' });
            
            if (data?.results) {
                setRequests(data.results);
                setTotalCount(data.count || 0);
            } else if (Array.isArray(data)) {
                setRequests(data);
                setTotalCount(data.length);
            } else {
                setRequests([]);
                setTotalCount(0);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleApprove = async (req) => {
        const result = await Swal.fire({
            title: '¿Aprobar solicitud?',
            text: `Se aplicarán los cambios a ${req.tipo_entidad} #${req.entidad_id}`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: 'var(--success-green)',
            confirmButtonText: 'Sí, aprobar'
        });

        if (result.isConfirmed) {
            try {
                await requestsService.approve(req.id);
                Swal.fire('Aprobado', 'Los cambios se han aplicado exitosamente.', 'success');
                loadRequests();
                setSelectedRequest(null);
            } catch (error) {
                Swal.fire('Error', 'No se pudo aprobar la solicitud.', 'error');
            }
        }
    };

    const handleReject = async (req) => {
        const { value: motivo } = await Swal.fire({
            title: 'Rechazar Solicitud',
            input: 'textarea',
            inputLabel: 'Motivo del rechazo',
            inputPlaceholder: 'Ingresa el motivo...',
            showCancelButton: true,
            confirmButtonColor: 'var(--danger-red)'
        });

        if (motivo) {
            try {
                await requestsService.reject(req.id, motivo);
                Swal.fire('Rechazado', 'La solicitud ha sido rechazada.', 'success');
                loadRequests();
                setSelectedRequest(null);
            } catch (error) {
                Swal.fire('Error', 'No se pudo rechazar la solicitud.', 'error');
            }
        }
    };

    // Paginamos solo las pendientes (ya vienen del backend así)
    const displayRequests = requests;

    const solicitudesTourSteps = [
        {
            element: '#tour-solicitudes-header',
            intro: '<strong>Bandeja de Autorizaciones</strong>Aquí llegarán los avisos cuando tus vendedores necesiten hacer un descuento mayor al permitido. Si lo apruebas, podrán cerrar la venta al instante.',
            position: 'bottom',
        }
    ];

    const handleTourComplete = () => {
        if (user?.rol === 'ADMIN') {
            window.location.href = '/app/empleados?tour=empleados';
        } else {
            // Si no es admin, salta todo y se va al módulo al Dashboard principal
            window.location.href = '/app/cotizaplus?tour=dashboard';
        }
    };

    return (
        <div className="container-custom fade-in-up" style={{ maxWidth: '1000px', width: '100%', padding: '2rem', margin: '0 auto', overflowY: 'auto' }}>
            {/* 🌟 Capa del Tour Guiado Inyectada 🌟 */}
            {runTour && (
                <AppTour
                    steps={solicitudesTourSteps}
                    runTour={runTour}
                    setRunTour={setRunTour}
                    tourOptions={{ doneLabel: user?.rol === 'ADMIN' ? 'Continuar a Empleados' : 'Finalizar' }}
                    onTourComplete={handleTourComplete}
                />
            )}

            <div className="card-header-custom" style={{ marginTop: '0', borderBottom: 'none', paddingBottom: '0' }} id="tour-solicitudes-header">
                <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                        <h2 style={{ marginBottom: 0 }}>Solicitudes de Cambio</h2>
                        <SectionHelpModal
                            title="Bandeja de Autorizaciones"
                            content={
                                <div>
                                    <p>Esta es tu bandeja de entrada de permisos. Aquí llegan las peticiones de tus Vendedores (Asesores) cuando intentan realizar una acción restringida:</p>
                                    <ul style={{ paddingLeft: '20px', marginTop: '15px' }}>
                                        <li style={{ marginBottom: '10px' }}><strong>Aprobación de Descuentos:</strong> Si un Asesor intenta vender un producto por debajo del precio mínimo autorizado, la cotización quedará "Pendiente" y te llegará una solicitud aquí.</li>
                                        <li><strong>Impacto Inmediato:</strong> Al momento en que haces clic en "Aprobar" o "Rechazar" en esta pantalla, el sistema automáticamente libera la cotización original y le notifica al Asesor para que pueda cerrar la venta con su cliente.</li>
                                    </ul>
                                </div>
                            }
                        />
                    </div>
                    <p className="text-muted" style={{ marginTop: '5px' }}>Revisa y aprueba las modificaciones realizadas por tu equipo</p>
                </div>
            </div>

            <div className="card-custom">
                {loading && requests.length === 0 ? (
                    <div style={{ padding: '20px', textAlign: 'center' }}>Cargando...</div>
                ) : (
                    <>
                        <div className="table-responsive d-none d-lg-block">
                            <table className="table-custom">
                                <thead>
                                    <tr>
                                        <th>Empleado</th>
                                        <th>Fecha</th>
                                        <th>Área</th>
                                        <th>Estado</th>
                                        <th style={{ textAlign: 'right' }}>Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {displayRequests.length === 0 ? (
                                        <tr><td colSpan="5" style={{ textAlign: 'center', padding: '20px' }}>No hay solicitudes pendientes.</td></tr>
                                    ) : (
                                        displayRequests.map(req => (
                                            <tr key={req.id}>
                                                <td>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                        <FiUser />
                                                        <span>{req.solicitante_info?.first_name} {req.solicitante_info?.last_name}</span>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                        <FiClock />
                                                        {new Date(req.fecha_solicitud).toLocaleString('es-CL')}
                                                    </div>
                                                </td>
                                                <td>
                                                    <span className={`badge-custom ${req.tipo_entidad === 'PRODUCTO' ? 'badge-primary' : 'badge-warning'}`}>
                                                        {req.tipo_entidad === 'PRODUCTO' ? <FiBox /> : <FiFileText />} {req.tipo_entidad}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className="badge-custom badge-secondary">PENDIENTE</span>
                                                </td>
                                                <td style={{ textAlign: 'right' }}>
                                                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '5px' }}>
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                const route = req.tipo_entidad === 'PRODUCTO'
                                                                    ? `/app/cotizaplus/productos/editar/${req.entidad_id}`
                                                                    : `/app/cotizaplus/cotizaciones/editar/${req.entidad_id}`;
                                                                navigate(`${route}?requestId=${req.id}`);
                                                            }}
                                                            title="Revisar"
                                                            style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: '5px', color: 'var(--primary-orange)' }}
                                                        >
                                                            <FiEye size={20} />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                        <div className="d-block d-lg-none mt-3">
                            {displayRequests.length === 0 ? (
                                <div style={{ textAlign: 'center', padding: '20px' }} className="text-muted">No hay solicitudes pendientes.</div>
                            ) : (
                                displayRequests.map(req => (
                                    <div key={req.id} className="mobile-card">
                                        <div className="mobile-card-header">
                                            <span className="mobile-card-id" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><FiUser /> {req.solicitante_info?.first_name} {req.solicitante_info?.last_name}</span>
                                            <span className="badge-custom badge-secondary">PENDIENTE</span>
                                        </div>
                                        <div className="mobile-card-body">
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                                                <FiClock /> {new Date(req.fecha_solicitud).toLocaleString('es-CL')}
                                            </div>
                                            <div>
                                                <span className={`badge-custom ${req.tipo_entidad === 'PRODUCTO' ? 'badge-primary' : 'badge-warning'}`}>
                                                    {req.tipo_entidad === 'PRODUCTO' ? <FiBox /> : <FiFileText />} {req.tipo_entidad}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="mobile-card-footer" style={{ borderTop: 'none', paddingTop: 0, justifyContent: 'flex-end', minHeight: 'unseT', paddingBottom: '16px' }}>
                                            <button
                                                className="btn-custom"
                                                onClick={() => {
                                                    const route = req.tipo_entidad === 'PRODUCTO'
                                                        ? `/app/cotizaplus/productos/editar/${req.entidad_id}`
                                                        : `/app/cotizaplus/cotizaciones/editar/${req.entidad_id}`;
                                                    navigate(`${route}?requestId=${req.id}`);
                                                }}
                                                style={{ display: 'flex', alignItems: 'center', gap: '8px', backgroundColor: '#f3f4f6', color: '#4b5563', border: '1px solid #e5e7eb', padding: '6px 12px' }}
                                            >
                                                <FiEye size={18} /> Revisar
                                            </button>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>

                        <Pagination
                            currentPage={currentPage}
                            totalCount={totalCount}
                            onPageChange={(page) => setCurrentPage(page)}
                        />
                    </>
                )}
            </div>

            {/* Modal Detalle */}
            {selectedRequest && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
                }}>
                    <div className="card-custom" style={{ width: '90%', maxWidth: '600px', maxHeight: '90vh', overflowY: 'auto' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
                            <h3>Detalle Solicitud #{selectedRequest.id}</h3>
                            <button onClick={() => setSelectedRequest(null)} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer' }}><FiX /></button>
                        </div>

                        <div style={{ marginBottom: '20px' }}>
                            <p><strong>Empleado:</strong> {selectedRequest.solicitante_info?.first_name} {selectedRequest.solicitante_info?.last_name}</p>
                            <p><strong>Entidad:</strong> {selectedRequest.tipo_entidad} #{selectedRequest.entidad_id}</p>
                            <p><strong>Datos Propuestos:</strong></p>
                            <pre style={{
                                backgroundColor: '#f8f9fa',
                                padding: '10px',
                                borderRadius: '5px',
                                overflowX: 'auto',
                                fontSize: '12px'
                            }}>
                                {JSON.stringify(selectedRequest.datos_propuestos, null, 2)}
                            </pre>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                            <button className="btn-custom btn-secondary" onClick={() => setSelectedRequest(null)}>Cerrar</button>
                            <button className="btn-custom btn-danger" onClick={() => handleReject(selectedRequest)}>Rechazar</button>
                            <button className="btn-custom btn-primary" onClick={() => handleApprove(selectedRequest)}>Aprobar</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default RequestsPanel;
